"use client";

import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'app-view-mode';

export type ViewMode = 'normal' | 'pro';

function readStoredMode(): ViewMode | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'normal' || v === 'pro' ? v : null;
  } catch {
    return null;
  }
}

const noopSubscribe = () => () => {};

export function useProMode(defaultMode: ViewMode = 'pro') {
  // Server and hydration render see no stored mode, so server HTML always
  // matches; React re-renders with the stored mode right after hydrating.
  const stored = useSyncExternalStore(noopSubscribe, readStoredMode, () => null);
  const [chosen, setMode] = useState<ViewMode | null>(null);
  const mode: ViewMode = chosen ?? stored ?? defaultMode;

  // Listen to cross-tab changes
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && (e.newValue === 'normal' || e.newValue === 'pro')) {
        setMode(e.newValue as ViewMode);
      }
    };
    window.addEventListener('storage', handler);
    const localHandler = (e: Event) => {
      const next = (e as CustomEvent).detail as ViewMode | undefined;
      if ((next === 'normal' || next === 'pro') && next !== mode) setMode(next);
    };
    window.addEventListener('app-view-mode-change', localHandler as EventListener);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener('app-view-mode-change', localHandler as EventListener);
    };
  }, [mode]);

  const toggle = useCallback(() => {
    const next: ViewMode = mode === 'pro' ? 'normal' : 'pro';
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
    setMode(next);
  }, [mode]);

  const set = useCallback((next: ViewMode) => {
    setMode(() => {
      try { localStorage.setItem(STORAGE_KEY, next); } catch {}
      return next;
    });
  }, []);

  // Broadcast only explicit choices: a hydration-time default broadcast would
  // flip every other mounted instance away from the stored mode.
  useEffect(() => {
    if (!chosen) return;
    try { window.dispatchEvent(new CustomEvent('app-view-mode-change', { detail: chosen })); } catch {}
  }, [chosen]);

  return { mode, isPro: mode === 'pro', toggle, set };
}


